import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import axios from "axios";
import { API_BASE_URL, getApiToken } from "../config/authConfig";
import OnboardingExecutionProgress from "./OnboardingExecutionProgress";

async function fetchWithRetry(fn, retries = 3, delayMs = 1500) {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503 && i < retries - 1) {
        await new Promise((r) => setTimeout(r, delayMs * (i + 1)));
      } else {
        throw err;
      }
    }
  }
}

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const LOCATIONS = ["Philadelphia", "New York City", "Bakery", "Remote"];
const EMPLOYEE_TYPES = ["Full Time", "Contractor"];

// Office object shape: { OfficeName, Address, District, Region }
const REMOTE_OFFICE = { OfficeName: "Others", Address: "", District: "", Region: "" };

export default function OnboardingForm({ onSubmitted, onViewHistory }) {
  const { accounts, instance } = useMsal();
  const user = accounts[0] || {};
  const submittedBy = user.username || "";

  const [token, setToken] = useState(null);

  // Personal info
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const displayName = [firstName.trim(), lastName.trim()].filter(Boolean).join(" ");

  // Contact & employment
  const [contactNumber, setContactNumber] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [employeeType, setEmployeeType] = useState("");

  // UPN
  const [upn, setUpn] = useState("");
  const [upnValidating, setUpnValidating] = useState(false);
  const [upnResult, setUpnResult] = useState(null);
  const [upnResolution, setUpnResolution] = useState(null); // "use-suggested" | "keep-original"

  // Department
  const [departments, setDepartments] = useState([]);
  const [deptSearch, setDeptSearch] = useState("");
  const [deptOpen, setDeptOpen] = useState(false);
  const [selectedDept, setSelectedDept] = useState("");
  const [loadingDepts, setLoadingDepts] = useState(false);
  const deptRef = useRef(null);

  // Job Title
  const [jobTitles, setJobTitles] = useState([]);
  const [titleSearch, setTitleSearch] = useState("");
  const [titleOpen, setTitleOpen] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState("");
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [checkingLicenseTitle, setCheckingLicenseTitle] = useState("");
  const [noLicenseModal, setNoLicenseModal] = useState(null); // { licenseGroupName, message } | null
  const [licenseAvailableInfo, setLicenseAvailableInfo] = useState(null); // { licenseGroupName } | null
  const titleRef = useRef(null);

  // Manager
  const [managerQuery, setManagerQuery] = useState("");
  const [managerResults, setManagerResults] = useState([]);
  const [managerLoading, setManagerLoading] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const managerRef = useRef(null);
  const debouncedManagerQuery = useDebounce(managerQuery, 350);

  // Location & Office (office is a full object: { OfficeName, Address, District, Region })
  const [location, setLocation] = useState("");
  const [offices, setOffices] = useState([]);
  const [officeSearch, setOfficeSearch] = useState("");
  const [officeOpen, setOfficeOpen] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [loadingOffices, setLoadingOffices] = useState(false);
  const officeRef = useRef(null);

  // Applications
  const [apps, setApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [selectedApps, setSelectedApps] = useState(new Set());

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  // Execution progress overlay
  const [showProgress, setShowProgress] = useState(false);
  const [progressRunning, setProgressRunning] = useState(false);
  const [progressLog, setProgressLog] = useState("");
  const [progressTransaction, setProgressTransaction] = useState("");
  const [progressEmployee, setProgressEmployee] = useState("");
  const [progressPendingLicense, setProgressPendingLicense] = useState(false);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    getApiToken(instance).then(setToken);
  }, [instance]);

  // Load departments once token is ready
  useEffect(() => {
    if (!token) return;
    setLoadingDepts(true);
    fetchWithRetry(() =>
      axios.get(`${API_BASE_URL}/onboarding/getDepartments`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    )
      .then((res) => setDepartments(res.data.departments || []))
      .catch(() => {})
      .finally(() => setLoadingDepts(false));
  }, [token]);

  // Load job titles when department changes
  useEffect(() => {
    setNoLicenseModal(null);
    setCheckingLicenseTitle("");
    setLicenseAvailableInfo(null);
    if (!selectedDept || !token) {
      setJobTitles([]);
      setSelectedTitle("");
      setTitleSearch("");
      return;
    }
    setLoadingTitles(true);
    setSelectedTitle("");
    setTitleSearch("");
    axios
      .get(`${API_BASE_URL}/onboarding/getJobTitles`, {
        params: { department: selectedDept },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setJobTitles(res.data.jobTitles || []))
      .catch(() => {})
      .finally(() => setLoadingTitles(false));
  }, [selectedDept, token]);

  // Auto-generate UPN from first letter of firstName + lastName
  useEffect(() => {
    const local = `${firstName.charAt(0)}${lastName}`
      .toLowerCase()
      .replace(/[^a-z0-9.]/g, "");
    setUpn(local ? `${local}@insomniacookies.com` : "");
    setUpnResult(null);
    setUpnResolution(null);
  }, [firstName, lastName]);

  // Load offices when location changes
  useEffect(() => {
    setSelectedOffice(null);
    setOfficeSearch("");
    setOffices([]);

    if (!location) return;

    if (location === "Remote") {
      setSelectedOffice(REMOTE_OFFICE);
      return;
    }

    if (!token) return;
    setLoadingOffices(true);
    axios
      .get(`${API_BASE_URL}/onboarding/getOffices`, {
        params: { location },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const list = res.data.offices || [];
        setOffices(list);
        if (location === "Philadelphia" || location === "New York City") {
          setSelectedOffice(list[0] || null);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingOffices(false));
  }, [location, token]);

  // Load applications once token is ready
  useEffect(() => {
    if (!token) return;
    setLoadingApps(true);
    fetchWithRetry(() =>
      axios.get(`${API_BASE_URL}/onboarding/getApplications`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    )
      .then((res) => setApps(res.data.applications || []))
      .catch((err) => console.error("getApplications error:", err.response?.data || err.message))
      .finally(() => setLoadingApps(false));
  }, [token]);

  // Manager search
  useEffect(() => {
    if (!debouncedManagerQuery || debouncedManagerQuery.length < 3 || !token) {
      setManagerResults([]);
      setManagerOpen(false);
      return;
    }
    if (selectedManager) return;
    let cancelled = false;
    setManagerLoading(true);
    axios
      .get(`${API_BASE_URL}/onboarding/searchManagers`, {
        params: { query: debouncedManagerQuery },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!cancelled) {
          setManagerResults(res.data.managers || []);
          setManagerOpen(true);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setManagerLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedManagerQuery, token, selectedManager]);

  // Close all dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (deptRef.current && !deptRef.current.contains(e.target)) setDeptOpen(false);
      if (titleRef.current && !titleRef.current.contains(e.target)) setTitleOpen(false);
      if (managerRef.current && !managerRef.current.contains(e.target)) setManagerOpen(false);
      if (officeRef.current && !officeRef.current.contains(e.target)) setOfficeOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Auto-validate UPN 800 ms after it stops changing (covers both auto-generation and manual edits)
  const debouncedUpn = useDebounce(upn, 800);
  useEffect(() => {
    if (!debouncedUpn || !debouncedUpn.includes("@") || !token) return;
    let cancelled = false;
    setUpnValidating(true);
    setUpnResult(null);
    setUpnResolution(null);
    axios
      .get(`${API_BASE_URL}/onboarding/validateUPN`, {
        params: { upn: debouncedUpn },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => { if (!cancelled) setUpnResult(res.data); })
      .catch(() => { if (!cancelled) setUpnResult(null); })
      .finally(() => { if (!cancelled) setUpnValidating(false); });
    return () => { cancelled = true; };
  }, [debouncedUpn, token]);

  const resolvedUpn = upnResult?.exists
    ? upnResolution === "use-suggested"
      ? upnResult.suggestedUPN
      : upnResolution === "keep-original"
      ? upn
      : null
    : upn || null;

  const toggleApp = (app) => {
    setSelectedApps((prev) => {
      const next = new Set(prev);
      if (next.has(app)) next.delete(app);
      else next.add(app);
      return next;
    });
  };

  const filteredDepts = departments.filter((d) =>
    d.toLowerCase().includes(deptSearch.toLowerCase())
  );

  const filteredTitles = jobTitles.filter((t) =>
    t.toLowerCase().includes(titleSearch.toLowerCase())
  );

  const dismissNoLicenseModal = () => {
    setNoLicenseModal(null);
    setSelectedTitle("");
    setTitleSearch("");
    setTitleOpen(false);
  };

  const handleSelectTitle = async (t) => {
    setTitleSearch("");
    setTitleOpen(false);
    setCheckingLicenseTitle(t);
    setLicenseAvailableInfo(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/onboarding/checkLicenseAvailability`, {
        params: { jobTitle: t },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.available === false) {
        setNoLicenseModal({
          licenseGroupName: res.data.licenseGroupName,
          message: res.data.message,
        });
        setSelectedTitle("");
        return;
      }
      setLicenseAvailableInfo({ licenseGroupName: res.data.licenseGroupName });
    } catch {
      // If the availability check itself fails, don't block the user from proceeding
    } finally {
      setCheckingLicenseTitle("");
    }
    setSelectedTitle(t);
    setValidationErrors((p) => ({ ...p, jobTitle: undefined }));
  };

  const filteredOffices = offices.filter((o) =>
    o.OfficeName.toLowerCase().includes(officeSearch.toLowerCase())
  );

  const officeReadOnly =
    location === "Philadelphia" || location === "New York City" || location === "Remote";

  let upnConflictType = "";
  if (upnResult?.existsAsUPN && upnResult?.existsAsAlias) {
    upnConflictType = "as both a UPN and alias";
  } else if (upnResult?.existsAsUPN) {
    upnConflictType = "as an existing UPN";
  } else if (upnResult?.existsAsAlias) {
    upnConflictType = "as an existing email alias";
  }

  const validate = () => {
    const errors = {};
    if (!firstName.trim()) errors.firstName = "Required";
    if (!lastName.trim()) errors.lastName = "Required";
    if (!upn) {
      errors.upn = "Required";
    } else if (upnResult?.exists && !upnResolution) {
      errors.upn = "Please resolve the UPN conflict before submitting";
    }
    if (!startDate) errors.startDate = "Required";
    if (!employeeType) errors.employeeType = "Required";
    if (!selectedDept) errors.department = "Required";
    if (!selectedTitle) errors.jobTitle = "Required";
    if (!selectedManager) errors.manager = "Required";
    if (!location) errors.location = "Required";
    if (!selectedOffice?.OfficeName) errors.office = "Required";
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    setIsSubmitting(true);

    try {
      // Step 1 — Write to SharePoint
      const submitRes = await axios.post(
        `${API_BASE_URL}/onboarding/submit`,
        {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          displayName,
          userPrincipalName: resolvedUpn,
          employeeId: employeeId.trim(),
          contactNumber: contactNumber.trim(),
          startDate,
          employeeType,
          department: selectedDept,
          jobTitle: selectedTitle,
          manager: selectedManager.userPrincipalName,
          location,
          office: selectedOffice.OfficeName,
          address: selectedOffice.Address,
          district: selectedOffice.District,
          region: selectedOffice.Region,
          applications: [...selectedApps].join(", "),
          submittedBy,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { transactionNumber, itemId, dateSubmitted } = submitRes.data;
      const employeeName = displayName;

      // Step 2 — Show progress overlay and begin polling
      setProgressTransaction(transactionNumber);
      setProgressEmployee(employeeName);
      setProgressLog("");
      setProgressRunning(true);
      setProgressPendingLicense(false);
      setShowProgress(true);

      pollIntervalRef.current = setInterval(async () => {
        try {
          const t = await getApiToken(instance);
          const statusRes = await axios.get(`${API_BASE_URL}/onboarding/status`, {
            params: { itemId },
            headers: { Authorization: `Bearer ${t}` },
          });
          setProgressLog(statusRes.data.transactionLog || "");
        } catch { /* ignore poll errors */ }
      }, 5000);

      // Step 3 — Run automation (long-running)
      const execRes = await axios.post(
        `${API_BASE_URL}/onboarding/execute`,
        { itemId, transactionNumber, submittedBy },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, timeout: 600000 }
      );

      clearInterval(pollIntervalRef.current);
      const execStatus = execRes.data.status;
      const isPending  = execStatus === "Pending License";

      setProgressLog(execRes.data.transactionLog || "");
      setProgressRunning(false);
      setProgressPendingLicense(isPending);

      // Step 4 — Brief pause then navigate
      await new Promise((r) => setTimeout(r, isPending ? 4000 : 2000));
      setShowProgress(false);

      onSubmitted({
        transactionNumber,
        displayName,
        userPrincipalName: resolvedUpn,
        employeeId: employeeId.trim(),
        contactNumber: contactNumber.trim(),
        startDate,
        employeeType,
        department: selectedDept,
        jobTitle: selectedTitle,
        manager: selectedManager.displayName,
        location,
        office: selectedOffice.OfficeName,
        address: selectedOffice.Address,
        district: selectedOffice.District,
        region: selectedOffice.Region,
        applications: [...selectedApps].join(", "),
        dateSubmitted,
        executionStatus: execStatus,
      });
    } catch (err) {
      clearInterval(pollIntervalRef.current);
      setShowProgress(false);
      setProgressRunning(false);
      setSubmitError(err.response?.data?.error || "Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {showProgress && (
        <OnboardingExecutionProgress
          transactionNumber={progressTransaction}
          employeeName={progressEmployee}
          transactionLog={progressLog}
          isRunning={progressRunning}
          isPendingLicense={progressPendingLicense}
        />
      )}
      {noLicenseModal && (
        <div className="exec-overlay">
          <div className="exec-modal">
            <div className="exec-header exec-header-warning">
              <div className="exec-header-text">
                <h2>No License Seats Available</h2>
              </div>
            </div>
            <div className="exec-body">
              <div className="exec-pending-license-note">
                <p>
                  <strong>{noLicenseModal.licenseGroupName}</strong> has no available seats
                  for this job title.
                </p>
                <p>{noLicenseModal.message}</p>
                <p>
                  Please email{" "}
                  <a href="mailto:ytoledo@insomniacookies.com">ytoledo@insomniacookies.com</a>{" "}
                  to purchase the required license before selecting this job title.
                </p>
              </div>
            </div>
            <div className="exec-footer">
              <button type="button" className="btn-primary" onClick={dismissNoLicenseModal}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    <div className="page-wrapper">
      <header className="app-header">
        <div className="header-inner">
          <div className="header-brand">
            <span className="header-title">Employee Onboarding</span>
          </div>
          <div className="header-actions">
            <button className="btn-header-ghost btn-header" onClick={onViewHistory}>
              View History
            </button>
            <span className="header-username">{submittedBy}</span>
          </div>
        </div>
      </header>

      <main className="form-container">
        <form className="form-card" onSubmit={handleSubmit} noValidate>
          <div className="form-card-header">
            <h2>New Employee Onboarding Request</h2>
            <p>Fill in the details below to create a new employee account request.</p>
          </div>

          {/* ── Personal Information ─────────────────────────────────────────── */}
          <div className="form-section">
            <p className="section-title">Personal Information</p>
            <div className="fields-grid">
              <div className="form-field">
                <label className="field-label">
                  First Name <span className="required-star">*</span>
                </label>
                <input
                  type="text"
                  className={`field-input${validationErrors.firstName ? " field-input-error" : ""}`}
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setValidationErrors((p) => ({ ...p, firstName: undefined }));
                  }}
                  placeholder="e.g. Jane"
                />
                {validationErrors.firstName && (
                  <p className="field-error">{validationErrors.firstName}</p>
                )}
              </div>
              <div className="form-field">
                <label className="field-label">
                  Last Name <span className="required-star">*</span>
                </label>
                <input
                  type="text"
                  className={`field-input${validationErrors.lastName ? " field-input-error" : ""}`}
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    setValidationErrors((p) => ({ ...p, lastName: undefined }));
                  }}
                  placeholder="e.g. Smith"
                />
                {validationErrors.lastName && (
                  <p className="field-error">{validationErrors.lastName}</p>
                )}
              </div>
            </div>
            <div className="fields-grid" style={{ marginTop: 4 }}>
              <div className="form-field">
                <label className="field-label">Display Name</label>
                <div className="field-readonly">
                  {displayName || (
                    <span style={{ color: "var(--text-muted)" }}>Auto-filled from First + Last Name</span>
                  )}
                </div>
              </div>
              <div className="form-field">
                <label className="field-label">Contact Number</label>
                <input
                  type="tel"
                  className="field-input"
                  value={contactNumber}
                  onChange={(e) => setContactNumber(e.target.value)}
                  placeholder="e.g. +1 (215) 555-0100"
                />
              </div>
            </div>
          </div>

          {/* ── Account ──────────────────────────────────────────────────────── */}
          <div className="form-section">
            <p className="section-title">Account</p>
            <div className="form-field">
              <label className="field-label">
                User Principal Name (UPN) <span className="required-star">*</span>
              </label>
              <div className="search-wrapper">
                <input
                  type="text"
                  className={`field-input${validationErrors.upn ? " field-input-error" : ""}`}
                  value={upn}
                  onChange={(e) => {
                    setUpn(e.target.value);
                    setUpnResult(null);
                    setUpnResolution(null);
                    setValidationErrors((p) => ({ ...p, upn: undefined }));
                  }}
                  placeholder="Auto-generated from name"
                />
                {upnValidating && <span className="search-spinner" />}
              </div>
              {validationErrors.upn && (
                <p className="field-error">{validationErrors.upn}</p>
              )}
              {upnResult && !upnResult.exists && (
                <p className="field-hint-ok">UPN is available</p>
              )}
              {upnResult?.exists && (
                <div className="upn-conflict-box">
                  <p className="upn-conflict-msg">
                    <strong>{upn}</strong> already exists {upnConflictType} in Entra ID.
                    Choose how to proceed:
                  </p>
                  <label className="upn-radio-label">
                    <input
                      type="radio"
                      name="upnResolution"
                      value="use-suggested"
                      checked={upnResolution === "use-suggested"}
                      onChange={() => {
                        setUpnResolution("use-suggested");
                        setValidationErrors((p) => ({ ...p, upn: undefined }));
                      }}
                    />
                    Use suggested UPN: <strong>{upnResult.suggestedUPN}</strong>
                  </label>
                  <label className="upn-radio-label">
                    <input
                      type="radio"
                      name="upnResolution"
                      value="keep-original"
                      checked={upnResolution === "keep-original"}
                      onChange={() => {
                        setUpnResolution("keep-original");
                        setValidationErrors((p) => ({ ...p, upn: undefined }));
                      }}
                    />
                    I will manually resolve the conflict and use{" "}
                    <strong>{upn}</strong>
                  </label>
                </div>
              )}
            </div>
            <div className="form-field">
              <label className="field-label">Employee ID</label>
              <input
                type="text"
                className="field-input"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                placeholder="e.g. EE0123 (leave blank to use EE0000)"
              />
            </div>
          </div>

          {/* ── Employment Details ────────────────────────────────────────────── */}
          <div className="form-section">
            <p className="section-title">Employment Details</p>
            <div className="fields-grid">
              <div className="form-field">
                <label className="field-label">
                  Employee Type <span className="required-star">*</span>
                </label>
                <select
                  className={`field-input${validationErrors.employeeType ? " field-input-error" : ""}`}
                  value={employeeType}
                  onChange={(e) => {
                    setEmployeeType(e.target.value);
                    setValidationErrors((p) => ({ ...p, employeeType: undefined }));
                  }}
                >
                  <option value="">Select type...</option>
                  {EMPLOYEE_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                {validationErrors.employeeType && (
                  <p className="field-error">{validationErrors.employeeType}</p>
                )}
              </div>
              <div className="form-field">
                <label className="field-label">
                  Start Date <span className="required-star">*</span>
                </label>
                <input
                  type="date"
                  className={`field-input${validationErrors.startDate ? " field-input-error" : ""}`}
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setValidationErrors((p) => ({ ...p, startDate: undefined }));
                  }}
                />
                {validationErrors.startDate && (
                  <p className="field-error">{validationErrors.startDate}</p>
                )}
              </div>
            </div>
          </div>

          {/* ── Role ─────────────────────────────────────────────────────────── */}
          <div className="form-section">
            <p className="section-title">Role</p>

            {/* Department */}
            <div className="form-field" ref={deptRef}>
              <label className="field-label">
                Department <span className="required-star">*</span>
              </label>
              <div className="search-wrapper">
                <input
                  type="text"
                  className={`field-input${validationErrors.department ? " field-input-error" : ""}`}
                  placeholder={loadingDepts ? "Loading..." : "Search departments..."}
                  value={selectedDept || deptSearch}
                  onChange={(e) => {
                    setDeptSearch(e.target.value);
                    setSelectedDept("");
                    setSelectedTitle("");
                    setJobTitles([]);
                    setDeptOpen(true);
                    setValidationErrors((p) => ({ ...p, department: undefined }));
                  }}
                  onFocus={() => !selectedDept && setDeptOpen(true)}
                  disabled={loadingDepts}
                  autoComplete="off"
                />
                {loadingDepts && <span className="search-spinner" />}
              </div>
              {validationErrors.department && (
                <p className="field-error">{validationErrors.department}</p>
              )}
              {deptOpen && filteredDepts.length > 0 && (
                <ul className="search-dropdown">
                  {filteredDepts.map((d) => (
                    <li
                      key={d}
                      className="search-result"
                      onMouseDown={() => {
                        setSelectedDept(d);
                        setDeptSearch("");
                        setDeptOpen(false);
                        setValidationErrors((p) => ({ ...p, department: undefined }));
                      }}
                    >
                      <span className="result-name">{d}</span>
                    </li>
                  ))}
                </ul>
              )}
              {deptOpen && filteredDepts.length === 0 && !loadingDepts && (
                <ul className="search-dropdown">
                  <li className="search-result search-no-results">No departments found</li>
                </ul>
              )}
            </div>

            {/* Job Title */}
            <div className="form-field" ref={titleRef}>
              <label className="field-label">
                Job Title <span className="required-star">*</span>
              </label>
              <div className="search-wrapper">
                <input
                  type="text"
                  className={`field-input${validationErrors.jobTitle ? " field-input-error" : ""}`}
                  placeholder={
                    !selectedDept
                      ? "Select a department first"
                      : loadingTitles
                      ? "Loading..."
                      : checkingLicenseTitle
                      ? "Checking license availability..."
                      : "Search job titles..."
                  }
                  value={selectedTitle || titleSearch}
                  onChange={(e) => {
                    setTitleSearch(e.target.value);
                    setSelectedTitle("");
                    setTitleOpen(true);
                    setLicenseAvailableInfo(null);
                    setValidationErrors((p) => ({ ...p, jobTitle: undefined }));
                  }}
                  onFocus={() => selectedDept && !selectedTitle && setTitleOpen(true)}
                  disabled={!selectedDept || loadingTitles || !!checkingLicenseTitle}
                  autoComplete="off"
                />
                {(loadingTitles || checkingLicenseTitle) && <span className="search-spinner" />}
              </div>
              {validationErrors.jobTitle && (
                <p className="field-error">{validationErrors.jobTitle}</p>
              )}
              {licenseAvailableInfo && !validationErrors.jobTitle && (
                <p className="license-available-badge">
                  License available — {licenseAvailableInfo.licenseGroupName}
                </p>
              )}
              {titleOpen && filteredTitles.length > 0 && (
                <ul className="search-dropdown">
                  {filteredTitles.map((t) => (
                    <li
                      key={t}
                      className="search-result"
                      onMouseDown={() => handleSelectTitle(t)}
                    >
                      <span className="result-name">{t}</span>
                    </li>
                  ))}
                </ul>
              )}
              {titleOpen && filteredTitles.length === 0 && !loadingTitles && selectedDept && (
                <ul className="search-dropdown">
                  <li className="search-result search-no-results">No job titles found</li>
                </ul>
              )}
            </div>

            {/* Manager */}
            <div className="form-field" ref={managerRef}>
              <label className="field-label">
                Manager <span className="required-star">*</span>
              </label>
              <div className="search-wrapper">
                <input
                  type="text"
                  className={`field-input${validationErrors.manager ? " field-input-error" : ""}`}
                  placeholder="Search by name or UPN..."
                  value={selectedManager ? selectedManager.displayName : managerQuery}
                  onChange={(e) => {
                    setManagerQuery(e.target.value);
                    setSelectedManager(null);
                    setValidationErrors((p) => ({ ...p, manager: undefined }));
                  }}
                  autoComplete="off"
                />
                {managerLoading && <span className="search-spinner" />}
              </div>
              {validationErrors.manager && (
                <p className="field-error">{validationErrors.manager}</p>
              )}
              {selectedManager && (
                <p className="field-hint">
                  {selectedManager.userPrincipalName}
                  {selectedManager.jobTitle ? ` · ${selectedManager.jobTitle}` : ""}
                </p>
              )}
              {managerOpen && managerResults.length > 0 && !selectedManager && (
                <ul className="search-dropdown">
                  {managerResults.map((m) => (
                    <li
                      key={m.id}
                      className="search-result"
                      onMouseDown={() => {
                        setSelectedManager(m);
                        setManagerQuery("");
                        setManagerOpen(false);
                        setValidationErrors((p) => ({ ...p, manager: undefined }));
                      }}
                    >
                      <span className="result-name">{m.displayName}</span>
                      <span className="result-upn">
                        {m.userPrincipalName}
                        {m.jobTitle ? ` · ${m.jobTitle}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {managerOpen &&
                managerResults.length === 0 &&
                !managerLoading &&
                managerQuery.length >= 3 && (
                  <ul className="search-dropdown">
                    <li className="search-result search-no-results">No managers found</li>
                  </ul>
                )}
            </div>
          </div>

          {/* ── Work Location ─────────────────────────────────────────────────── */}
          <div className="form-section">
            <p className="section-title">Work Location</p>

            {/* Location dropdown */}
            <div className="form-field">
              <label className="field-label">
                Location <span className="required-star">*</span>
              </label>
              <select
                className={`field-input${validationErrors.location ? " field-input-error" : ""}`}
                value={location}
                onChange={(e) => {
                  setLocation(e.target.value);
                  setValidationErrors((p) => ({ ...p, location: undefined, office: undefined }));
                }}
              >
                <option value="">Select a location...</option>
                {LOCATIONS.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
              {validationErrors.location && (
                <p className="field-error">{validationErrors.location}</p>
              )}
            </div>

            {/* Office */}
            <div className="form-field" ref={officeRef}>
              <label className="field-label">
                Office <span className="required-star">*</span>
              </label>
              {officeReadOnly ? (
                <div className="field-readonly">
                  {loadingOffices
                    ? "Loading..."
                    : selectedOffice?.OfficeName || (location ? "—" : "Select a location first")}
                </div>
              ) : (
                <div className="search-wrapper">
                  <input
                    type="text"
                    className={`field-input${validationErrors.office ? " field-input-error" : ""}`}
                    placeholder={
                      !location
                        ? "Select a location first"
                        : loadingOffices
                        ? "Loading offices..."
                        : "Search offices..."
                    }
                    value={selectedOffice ? selectedOffice.OfficeName : officeSearch}
                    onChange={(e) => {
                      setOfficeSearch(e.target.value);
                      setSelectedOffice(null);
                      setOfficeOpen(true);
                      setValidationErrors((p) => ({ ...p, office: undefined }));
                    }}
                    onFocus={() => location && !selectedOffice && setOfficeOpen(true)}
                    disabled={!location || loadingOffices}
                    autoComplete="off"
                  />
                  {loadingOffices && <span className="search-spinner" />}
                  {officeOpen && filteredOffices.length > 0 && (
                    <ul className="search-dropdown">
                      {filteredOffices.map((o) => (
                        <li
                          key={o.OfficeName}
                          className="search-result"
                          onMouseDown={() => {
                            setSelectedOffice(o);
                            setOfficeSearch("");
                            setOfficeOpen(false);
                            setValidationErrors((p) => ({ ...p, office: undefined }));
                          }}
                        >
                          <span className="result-name">{o.OfficeName}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {officeOpen && filteredOffices.length === 0 && !loadingOffices && location && (
                    <ul className="search-dropdown">
                      <li className="search-result search-no-results">No offices found</li>
                    </ul>
                  )}
                </div>
              )}
              {validationErrors.office && (
                <p className="field-error">{validationErrors.office}</p>
              )}
            </div>

            {/* Address / District / Region — auto-filled from selected office */}
            {selectedOffice && (
              <div className="auto-populated-fields">
                <div className="fields-grid">
                  <div className="form-field">
                    <label className="field-label">Address</label>
                    <div className="field-readonly">
                      {selectedOffice.Address || <span style={{ color: "var(--text-muted)" }}>—</span>}
                    </div>
                  </div>
                  <div className="form-field">
                    <label className="field-label">District</label>
                    <div className="field-readonly">
                      {selectedOffice.District || <span style={{ color: "var(--text-muted)" }}>—</span>}
                    </div>
                  </div>
                  <div className="form-field">
                    <label className="field-label">Region</label>
                    <div className="field-readonly">
                      {selectedOffice.Region || <span style={{ color: "var(--text-muted)" }}>—</span>}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Applications ──────────────────────────────────────────────────── */}
          <div className="form-section">
            <p className="section-title">Applications</p>
            <p className="section-description">
              Select any additional applications this employee will need access to.
            </p>
            <div className="apps-checklist">
              {loadingApps && <span className="search-spinner" style={{ marginLeft: 4 }} />}
              {!loadingApps && apps.length === 0 && (
                <span style={{ color: "var(--text-muted)", fontSize: 14 }}>No applications configured.</span>
              )}
              {apps.map(({ name }) => (
                <label key={name} className="app-checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedApps.has(name)}
                    onChange={() => toggleApp(name)}
                  />
                  {name}
                </label>
              ))}
            </div>
          </div>

          {/* ── Submitted By ──────────────────────────────────────────────────── */}
          <div className="form-section">
            <p className="section-title">Submitted By</p>
            <div className="form-field">
              <label className="field-label">Requestor</label>
              <div className="field-readonly">{submittedBy}</div>
            </div>
          </div>

          {submitError && <div className="submit-error">{submitError}</div>}

          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary btn-submit"
              disabled={isSubmitting || !token}
            >
              {isSubmitting ? (
                <>
                  <span className="btn-spinner" />
                  Submitting...
                </>
              ) : (
                "Submit Onboarding Request"
              )}
            </button>
          </div>
        </form>
      </main>
    </div>
    </>
  );
}
